/* =============================================================
   Select Anime — Swipe Rail (phone only)
   Design contract: design/スワイプ商品レール-設計契約-2026-09-04.md

   Responsibilities
     - arrows, slide counter, Hide / Show toggle
     - ?ref=swipe-<arm> on every slide link (arm is only known client side)
     - the whole measurement plan of the contract, funnelled through ONE
       function (saSwipeTrack) so the transport can be swapped in one place
     - "See all N items" scrolls to the product grid
     - v1.1: the endless feed. Once the shopper has actually swiped in, more
       in-stock products are fetched 48 at a time from sections/sa-swipe-feed
       and appended, so a character page with 1,258 products stops ending at 23.

   It does nothing at all above 749px: no listeners, no observers, no events,
   and - the point of v1.1 - not one feed request.
   The height arm and the hidden state are decided by the section's own inline
   script BEFORE this file runs, so nothing here can shift the layout.

   The feed is layered strictly on top of v1.0: every one of its stop
   conditions (no more pages, a 404, two failures in a row, the DOM ceiling,
   a browser asking for reduced data, no fetch support) degrades to exactly
   what v1.0 did, which is a rail of the first cards and a "See all" card.

   No dependencies. assets/sa-rail.js is deliberately NOT reused (that one has
   no scroll-snap support).
   ============================================================= */
(function () {
  'use strict';

  if (window._saSwipeInit) return;
  window._saSwipeInit = true;

  var MEDIA = '(max-width: 749px)';
  var SWIPE_THROTTLE_MS = 300;
  var SWIPE_EVENT_CAP = 60;

  /* Feed (v1.1). FIRST is how deep the shopper has to be before anything is
     fetched at all - far enough that a glance at the rail costs nothing. TAIL
     is the runway kept ahead of them afterwards. */
  var FEED_TRIGGER_FIRST = 10;
  var FEED_TRIGGER_TAIL = 8;
  var FEED_MAX_FAILS = 2;

  function phone() {
    try {
      return window.matchMedia && window.matchMedia(MEDIA).matches;
    } catch (e) {
      return false;
    }
  }

  function search() {
    try {
      return window.location.search || '';
    } catch (e) {
      return '';
    }
  }

  function assign(target, source) {
    for (var k in source) {
      if (Object.prototype.hasOwnProperty.call(source, k)) target[k] = source[k];
    }
    return target;
  }

  /* ---------- The single transport ------------------------- */
  /* Rides the gtag instance the Google & YouTube app already puts on the top
     frame. send_to keeps the events inside our own GA4 property. On a preview
     or development theme nothing is sent unless ?swipe_track=1 is present. */
  /* The gtag this rides on is defined by the Google & YouTube web pixel, which
     the web pixels manager loads asynchronously (runtimeContext OPEN). The
     view event fires as soon as the rail is on screen, which is often BEFORE
     that pixel has run. A plain dataLayer.push({event}) is not read by gtag.js
     (only gtag() commands are), so those early events used to be dropped.
     They now wait here for the existing gtag, in order, for a bounded time.
     Nothing global is defined: the pixel stays the only owner of window.gtag. */
  var GTAG_WAIT_MS = 250;
  var GTAG_WAIT_TRIES = 60; /* 15 s, then give up exactly as before */
  var GTAG_QUEUE_CAP = 30;

  function makeTracker(gaId) {
    var forced = /[?&]swipe_track=1(&|$)/.test(search());
    var queue = [];
    var waitTries = 0;
    var waiting = false;
    var gaveUp = false;

    function send(name, p) {
      var payload = assign({}, p);
      if (gaId) payload.send_to = gaId;
      window.gtag('event', name, payload);
    }

    function flush() {
      if (typeof window.gtag !== 'function') return false;
      var q = queue;
      queue = [];
      for (var i = 0; i < q.length; i++) {
        try { send(q[i][0], q[i][1]); } catch (e) {}
      }
      return true;
    }

    function wait() {
      waiting = false;
      if (flush()) return;
      waitTries += 1;
      if (waitTries >= GTAG_WAIT_TRIES) {
        gaveUp = true;
        queue = [];
        return;
      }
      waiting = true;
      window.setTimeout(wait, GTAG_WAIT_MS);
    }

    return function saSwipeTrack(name, params) {
      var p = params || {};
      try {
        var role = (window.Shopify && window.Shopify.theme && window.Shopify.theme.role) || '';
        if (role !== 'main' && !forced) {
          if (window.console && window.console.debug) window.console.debug('[sa-swipe]', name, p);
          return;
        }
        if (typeof window.gtag === 'function') {
          flush();
          send(name, p);
        } else if (!gaveUp && queue.length < GTAG_QUEUE_CAP) {
          queue.push([name, assign({}, p)]);
          if (!waiting) wait();
        }
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('sa_swipe', assign({ sa_event: name }, p));
        }
      } catch (e) {
        /* measurement must never break the page */
      }
    };
  }

  /* ---------- One rail ------------------------------------- */
  function init(root) {
    if (!root || root.getAttribute('data-sa-swipe-init') === '1') return;
    if (!phone()) return;
    root.setAttribute('data-sa-swipe-init', '1');

    var cfg = { items: 0, collection: '', hide_days: 60, ga_id: '', total: 0, max_total: 500, feed_section: '' };
    try {
      var raw = root.querySelector('script[data-sa-swipe-config]');
      if (raw) cfg = assign(cfg, JSON.parse(raw.textContent));
    } catch (e) {
      /* keep the defaults */
    }

    var variant = document.documentElement.classList.contains('sa-swipe--full') ? 'full' : 'p80';
    var track = makeTracker(cfg.ga_id);
    var base = { sa_variant: variant };

    function ev(name, params) {
      track('sa_swipe_' + name + '_' + variant, assign(assign({}, base), params || {}));
    }

    var rail = root.querySelector('[data-sa-swipe-track]');
    var slides = [].slice.call(root.querySelectorAll('[data-sa-swipe-slide]'));
    var endSlide = root.querySelector('[data-sa-swipe-end]');
    var prev = root.querySelector('[data-sa-swipe-prev]');
    var next = root.querySelector('[data-sa-swipe-next]');
    var cur = root.querySelector('[data-sa-swipe-cur]');
    if (!rail || !slides.length) return;

    /* productSlides is the running list the feed appends to. `slides` stays
       the first screenful, so the two can always be told apart. */
    var productSlides = slides.slice();
    var countNodes = [].slice.call(root.querySelectorAll('[data-sa-swipe-count]'));
    var endCountNodes = [].slice.call(root.querySelectorAll('[data-sa-swipe-endcount]'));

    /* ---- ?ref=swipe-<arm> on every slide link -------------- */
    var handles = {};

    function tagLink(a) {
      var url = a.getAttribute('data-url') || a.getAttribute('href') || '';
      if (url) a.setAttribute('href', url + (url.indexOf('?') === -1 ? '?' : '&') + 'ref=swipe-' + variant);
      var h = a.getAttribute('data-handle');
      if (h) handles[h] = true;
      a.addEventListener('click', onLinkClick);
    }

    function onLinkClick(e) {
      var el = e.currentTarget;
      ev('tap', {
        sa_index: parseInt(el.getAttribute('data-index'), 10) || 0,
        sa_handle: el.getAttribute('data-handle') || '',
        transport_type: 'beacon'
      });
    }

    var links = [].slice.call(root.querySelectorAll('[data-sa-swipe-link]'));
    for (var i = 0; i < links.length; i++) tagLink(links[i]);

    /* ---- Counter + arrows --------------------------------- */
    var index = 0;
    var swipeSent = 0;
    var lastSwipeAt = 0;
    var ticking = false;

    function cells() {
      return [].slice.call(rail.children);
    }

    function nearest() {
      var mid = rail.scrollLeft + rail.clientWidth / 2;
      var list = cells();
      var best = 0;
      var bestD = Infinity;
      for (var j = 0; j < list.length; j++) {
        var c = list[j];
        var center = c.offsetLeft + c.offsetWidth / 2;
        var d = Math.abs(center - mid);
        if (d < bestD) { bestD = d; best = j; }
      }
      return best;
    }

    function paint(newIndex, method) {
      if (newIndex === index) return;
      index = newIndex;
      if (cur) cur.textContent = String(Math.min(index + 1, productSlides.length));
      if (prev) prev.hidden = index <= 0;
      if (next) next.hidden = index >= cells().length - 1;
      var now = Date.now();
      if (swipeSent < SWIPE_EVENT_CAP && now - lastSwipeAt >= SWIPE_THROTTLE_MS) {
        lastSwipeAt = now;
        swipeSent += 1;
        ev('swipe', { sa_index: index + 1, sa_method: method || 'touch' });
      }
      maybeFeed();
    }

    rail.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          paint(nearest(), 'touch');
        });
      },
      { passive: true }
    );

    function step(delta) {
      var list = cells();
      var target = Math.max(0, Math.min(list.length - 1, index + delta));
      var c = list[target];
      if (!c) return;
      rail.scrollLeft = c.offsetLeft - (rail.clientWidth - c.offsetWidth) / 2;
      paint(target, 'arrow');
    }

    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) {
      next.addEventListener('click', function () {
        /* The arrow is also the manual "give me more" control, which is the
           only way the feed advances for a browser asking for reduced data. */
        if (index >= cells().length - 2) fetchNext();
        step(1);
      });
      next.hidden = cells().length <= 1;
    }

    /* ---- Hide / Show -------------------------------------- */
    var hideBtn = root.querySelector('[data-sa-swipe-hide]');
    var showBtn = root.querySelector('[data-sa-swipe-show]');

    if (hideBtn) {
      hideBtn.addEventListener('click', function () {
        document.documentElement.classList.remove('sa-swipe--shown');
        document.documentElement.classList.add('sa-swipe--hidden');
        try { window.localStorage.setItem('sa:swipe:hidden', String(Date.now())); } catch (e) {}
        ev('hide', {});
      });
    }

    if (showBtn) {
      showBtn.addEventListener('click', function () {
        document.documentElement.classList.remove('sa-swipe--hidden');
        document.documentElement.classList.add('sa-swipe--shown');
        try { window.localStorage.removeItem('sa:swipe:hidden'); } catch (e) {}
        ev('show', {});
      });
    }

    /* ---- Tap ---------------------------------------------- */
    /* Bound in tagLink() above, so a card the feed appends is measured on the
       same code path as a card that was in the page HTML. */

    /* ---- Favourite ---------------------------------------- */
    document.addEventListener('sa:wishlist:change', function (e) {
      try {
        var h = e && e.detail && e.detail.handle;
        if (!h || !handles[h]) return;
        if (window.SAWishlist && window.SAWishlist.has(h)) ev('fav', { sa_handle: h });
      } catch (err) {}
    });

    /* ---- End card: See all N items / Back to start --------- */
    function gridNode() {
      return document.querySelector('#product-grid .grid__item') || document.getElementById('ProductGridContainer');
    }

    var seeAll = root.querySelector('[data-sa-swipe-seeall]');
    if (seeAll) {
      seeAll.addEventListener('click', function () {
        ev('end', { sa_action: 'tap' });
        var g = document.getElementById('ProductGridContainer') || gridNode();
        if (g && g.scrollIntoView) g.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    var backBtn = root.querySelector('[data-sa-swipe-back]');
    if (backBtn) {
      backBtn.addEventListener('click', function () {
        ev('end', { sa_action: 'restart' });
        /* behavior auto on purpose: the track's CSS asks for smooth scrolling,
           and smoothly animating back past several hundred cards is a long,
           pointless ride. */
        try {
          if (rail.scrollTo) rail.scrollTo({ left: 0, behavior: 'auto' });
          else rail.scrollLeft = 0;
        } catch (e) {
          rail.scrollLeft = 0;
        }
      });
    }

    /* =========================================================
       v1.1 endless feed
       Fetches sections/sa-swipe-feed by section id, 48 in-stock products at a
       time, and appends the cards before the end card. Everything about it is
       opt-out safe: if any single piece is missing the rail simply stays the
       v1.0 rail.
       ========================================================= */
    var feedSection = root.getAttribute('data-sa-swipe-feed-section') || cfg.feed_section || '';
    var collectionTotal = parseInt(root.getAttribute('data-sa-swipe-total'), 10);
    if (!isFinite(collectionTotal)) collectionTotal = parseInt(cfg.total, 10) || 0;
    var maxTotal = parseInt(cfg.max_total, 10) || 500;

    var endLoad = root.querySelector('[data-sa-swipe-more]');
    var endLoadText = root.querySelector('[data-sa-swipe-more-text]');
    var endWrap = root.querySelector('[data-sa-swipe-endwrap]');

    /* A browser on a metered or slow connection asks for this. Honour it by
       never fetching on its own: the arrow and the end card still work as
       manual "give me more" controls. */
    var reducedData = false;
    try {
      reducedData = !!(window.matchMedia && window.matchMedia('(prefers-reduced-data: reduce)').matches);
    } catch (e) {}

    var feedOn = !!feedSection && !!window.fetch && !!window.DOMParser && collectionTotal > productSlides.length;
    var feedDone = !feedOn;
    var feedBusy = false;
    var feedNextPage = 1;
    var feedPages = 0; /* 0 until the first response says how many there are */
    var feedItems = 0; /* the honest in-stock total, same source */
    var feedFails = 0;
    var feedFetches = 0;
    var feedCtl = null;

    function commas(n) {
      return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    /* Every number the rail shows counts the same thing: products a shopper
       can actually buy. The counter and the hint say "23+" until the feed
       reports the real in-stock total, then the total itself. The end card
       never says "+", because it is only read once the feed has finished or
       given up; if it gave up before the first page landed, the honest number
       is how many cards are actually there.
       On a page with no feed the end card is left exactly as the Liquid wrote
       it - see the comment in sections/sa-swipe-rail.liquid. */
    function paintCount() {
      var label = feedItems
        ? commas(feedItems)
        : commas(productSlides.length) + (collectionTotal > productSlides.length ? '+' : '');
      for (var c = 0; c < countNodes.length; c++) countNodes[c].textContent = label;
      if (!feedOn) return;
      var endLabel = commas(feedItems || productSlides.length);
      for (var e = 0; e < endCountNodes.length; e++) endCountNodes[e].textContent = endLabel;
    }

    /* The section renders "Loading more" with a real ellipsis; keep that string
       in the Liquid where it can be read and translated, and only swap it for
       the manual wording from here. */
    var loadingLabel = endLoadText ? endLoadText.textContent : '';

    function paintEnd() {
      var pending = feedOn && !feedDone;
      if (endLoad) endLoad.hidden = !pending;
      if (endWrap) endWrap.hidden = pending;
      /* Three states, not two. "Loading more" is a promise, so it is only
         shown while a request is genuinely in flight. A browser asking for
         reduced data, or a request that just failed, gets a tappable "Load
         more" instead - otherwise a shopper with no signal would sit in front
         of "Loading more" for ever and lose the "See all" way out that v1.0
         always gave them. Two failures in a row set feedDone, which brings
         that card straight back. */
      var manual = reducedData || (!feedBusy && feedFails > 0);
      if (endLoadText) endLoadText.textContent = manual ? 'Load more' : loadingLabel;
      if (backBtn) backBtn.hidden = !feedOn;
    }

    /* Built from location.pathname so a tag URL such as
       /collections/tanjiro-kamado/cat-figure keeps its tag, and whatever the
       shopper sorted or filtered by comes along. availability is forced on: the
       rail must never hand anyone a sold out card. */
    function feedUrl(page) {
      var qs = ['section_id=' + encodeURIComponent(feedSection), 'page=' + page, 'filter.v.availability=1'];
      try {
        var params = new window.URLSearchParams(window.location.search);
        params.forEach(function (val, key) {
          if (key === 'sort_by' || (key.indexOf('filter.') === 0 && key !== 'filter.v.availability')) {
            qs.push(encodeURIComponent(key) + '=' + encodeURIComponent(val));
          }
        });
      } catch (e) {}
      return window.location.pathname + '?' + qs.join('&');
    }

    function wire(node, pos) {
      node.setAttribute('data-index', String(pos));
      node.setAttribute('aria-label', pos + ' of ' + (feedItems || collectionTotal));
      var a = node.querySelector('[data-sa-swipe-link]');
      if (a) {
        a.setAttribute('data-index', String(pos));
        tagLink(a);
      }
    }

    /* Returns the number of cards added, or null when the response carried no
       feed wrapper at all (a page that can never produce cards). */
    function absorb(html) {
      var doc = null;
      try {
        doc = new window.DOMParser().parseFromString(html, 'text/html');
      } catch (e) {
        return null;
      }
      var wrap = doc.querySelector('[data-sa-swipe-feed]');
      if (!wrap) return null;

      var p = parseInt(wrap.getAttribute('data-pages'), 10);
      var it = parseInt(wrap.getAttribute('data-items'), 10);
      if (isFinite(p) && p > 0) feedPages = p;
      if (isFinite(it) && it > 0) feedItems = it;

      var found = wrap.querySelectorAll('[data-sa-swipe-slide]');
      var added = 0;
      for (var f = 0; f < found.length; f++) {
        var h = found[f].getAttribute('data-handle');
        if (!h || handles[h]) continue; /* page 1 overlaps the first screenful */
        if (productSlides.length >= maxTotal) break;
        var node = document.importNode(found[f], true);
        if (endSlide) rail.insertBefore(node, endSlide);
        else rail.appendChild(node);
        productSlides.push(node);
        wire(node, productSlides.length);
        added += 1;
      }
      return added;
    }

    function fetchNext() {
      if (!feedOn || feedDone || feedBusy) return;
      if (productSlides.length >= maxTotal) { feedDone = true; paintEnd(); return; }
      if (feedPages && feedNextPage > feedPages) { feedDone = true; paintEnd(); return; }

      feedBusy = true;
      paintEnd(); /* a retry has to go back to saying "Loading more" */
      var page = feedNextPage;
      var opts = { credentials: 'same-origin', headers: { Accept: 'text/html' } };
      try {
        feedCtl = new window.AbortController();
        opts.signal = feedCtl.signal;
      } catch (e) {
        feedCtl = null;
      }

      window
        .fetch(feedUrl(page), opts)
        .then(function (r) {
          if (r.status === 404) { feedDone = true; return null; }
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (text) {
          feedBusy = false;
          if (text === null) { paintEnd(); return; }
          var added = absorb(text);
          if (added === null) { feedDone = true; paintEnd(); return; }
          feedFails = 0;
          feedFetches += 1;
          feedNextPage = page + 1;
          if (feedPages && feedNextPage > feedPages) feedDone = true;
          if (productSlides.length >= maxTotal) feedDone = true;
          paintCount();
          paintEnd();
          if (next) next.hidden = index >= cells().length - 1;
          ev('more', { sa_page: page, sa_loaded: productSlides.length });
          maybeFeed();
        })
        .catch(function () {
          feedBusy = false;
          feedFails += 1;
          if (feedFails >= FEED_MAX_FAILS) feedDone = true;
          paintEnd();
        });
    }

    function maybeFeed() {
      if (!feedOn || feedDone || feedBusy || reducedData) return;
      var n = productSlides.length;
      if (feedFetches === 0 ? index >= FEED_TRIGGER_FIRST : index >= n - FEED_TRIGGER_TAIL) fetchNext();
    }

    if (endLoad) endLoad.addEventListener('click', function () { fetchNext(); });

    window.addEventListener('pagehide', function () {
      try { if (feedCtl) feedCtl.abort(); } catch (e) {}
    });

    paintCount();
    paintEnd();

    /* ---- Visibility events -------------------------------- */
    if (!window.IntersectionObserver) {
      ev('view', { sa_items: cfg.items, sa_collection: cfg.collection });
      return;
    }

    var viewSent = false;
    var viewIO = new window.IntersectionObserver(
      function (entries) {
        for (var v = 0; v < entries.length; v++) {
          if (entries[v].isIntersecting && !viewSent) {
            viewSent = true;
            ev('view', { sa_items: cfg.items, sa_collection: cfg.collection });
            viewIO.disconnect();
          }
        }
      },
      { threshold: 0.25 }
    );
    viewIO.observe(root);

    if (endSlide) {
      var endSent = false;
      var endIO = new window.IntersectionObserver(
        function (entries) {
          for (var w = 0; w < entries.length; w++) {
            if (entries[w].isIntersecting && !endSent) {
              endSent = true;
              ev('end', { sa_action: 'reach' });
              endIO.disconnect();
            }
          }
        },
        { root: rail, threshold: 0.6 }
      );
      endIO.observe(endSlide);
    }

    var grid = gridNode();
    if (grid) {
      var gridSent = false;
      var gridIO = new window.IntersectionObserver(
        function (entries) {
          for (var g2 = 0; g2 < entries.length; g2++) {
            if (entries[g2].isIntersecting && !gridSent) {
              gridSent = true;
              ev('grid', {});
              gridIO.disconnect();
            }
          }
        },
        { threshold: 0.25 }
      );
      gridIO.observe(grid);
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-sa-swipe]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor: a re-rendered section arrives with a fresh DOM node. */
  document.addEventListener('shopify:section:load', boot);
})();
